/** A location on the screen. (0, 0) is the top left corner. */
export class Point {
  readonly x: number;
  readonly y: number;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  static origin: Point = new Point(0, 0);

  add(other: Point): Point {
    return new Point(this.x + other.x, this.y + other.y);
  }
}

export class Rectangle {
  readonly topLeft: Point;
  readonly width: number;
  readonly height: number;

  constructor(topLeft: Point, width: number, height: number) {
    this.topLeft = topLeft;
    this.width = width;
    this.height = height;
  }

  get top() {
    return this.topLeft.y;
  }

  get left() {
    return this.topLeft.x;
  }
}

/**
 * Keeps track of a DOM node's position with respect to its first
 * relatively or absolutely positioned ancestor. This ancestor is
 * not necessarily the immediate parent.
 */
export class PositionedNode {
  /**
   * Offset from the ancestor at the time the node is created.
   * This is calculated with respect to the top left corners of the nodes.
   *
   * We assume the width and the height of the component does not change,
   * but it can be moved around within the ancestor.
   */
  private readonly initialBoundingBox: Rectangle;

  /** Offset from the initial position. */
  readonly offset: Point;

  private constructor(
    initialBoundingBox: Rectangle,
    offset: Point = Point.origin
  ) {
    this.initialBoundingBox = initialBoundingBox;
    this.offset = offset;
  }

  /**
   * Get position information given an HTML node.
   *
   * @param node the node to compute position information for
   */
  static of(node: HTMLElement, offset: Point = Point.origin): PositionedNode {
    const initialOffset = new Point(node.offsetLeft, node.offsetTop);
    const box = new Rectangle(
      initialOffset,
      node.offsetWidth,
      node.offsetHeight
    );
    return new PositionedNode(box, offset);
  }

  get width(): number {
    return this.initialBoundingBox.width;
  }

  get height(): number {
    return this.initialBoundingBox.height;
  }

  /**
   * Offset from the ancestor at the time this node was created.
   * Calculated as the distance between the top left corners of the two nodes.
   */
  get initialOffset() {
    return this.initialBoundingBox.topLeft;
  }

  /**
   * Current offset from the ancestor.
   * Calculated as the distance between the top left corners of the two nodes.
   */
  get ancestorOffset(): Point {
    return new Point(
      this.initialBoundingBox.left + this.offset.x,
      this.initialBoundingBox.top + this.offset.y
    );
  }

  /**
   * Offset from the center of this node to the top left corner of the ancestor.
   * */
  get center(): Point {
    const ancestorOffset = this.ancestorOffset;
    return new Point(
      ancestorOffset.x + this.width / 2,
      ancestorOffset.y + this.height / 2
    );
  }

  /**
   * Set the offset from the initial position to the given offset.
   */
  setOffset(newOffset: Point): PositionedNode {
    return new PositionedNode(this.initialBoundingBox, newOffset);
  }
}
